import { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Grid,
    InputBase,
    Stack,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { OfferStatus, ProductCatalogue } from '@prisma/client';
import { Form, Formik } from 'formik';
import { PricingSalesConditions } from 'web/src/pages/AddTerminalPricingPage/PricingSalesConditions';

import { navigate } from '@redwoodjs/router';

import { InputBaseField } from 'src/components/common/form/FormInput';
import { SelectBaseField } from 'src/components/common/form/FormSelect';
import Loader from 'src/components/Loader/Loader';
import MainCard from 'src/components/MainCard/MainCard';
import ModalContent from 'src/components/ModalContent/ModalContent';
import { DEFAULT_BORDER } from 'src/constants';
import useGeneratePresignedUrl from 'src/hooks/useGeneratePresignedUrl';
import {
    PricingTerm,
    PricingType,
    deactivatePricingAlertMessage,
    updatePricingAlertMessage,
} from 'src/hooks/usePricings';
import usePricingsOffers from 'src/hooks/usePricings/usePricingsOffers';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

import PricingAccordion from '../AddTerminalPricingPage/PricingAccordion';
import { TerminalPricingOfferPartnersModal } from '../AddTerminalPricingPage/TerminalPricingPartners';

type EditTerminalPricingProps = {
    id: string;
};

const EditOnlinePricingPage = ({ id }: EditTerminalPricingProps) => {
    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Product ID', minWidth: 150 },
        {
            field: 'imageUrl',
            headerName: 'Image',
            minWidth: 200,
            renderCell: (params) => {
                const { validateAwsURL } = useGeneratePresignedUrl(); // Hook is defined inside here due to infinate rendering when taken outside
                const [imageUrl, setImageUrl] = useState('');

                useEffect(() => {
                    const fetchPresignedUrl = async () => {
                        const url = params.row.imageUrl;
                        const presignedUrl = await validateAwsURL(url);
                        setImageUrl(presignedUrl ?? url);
                    };
                    fetchPresignedUrl();
                }, [params.row.imageUrl]);

                return (
                    <Box sx={{ padding: '10px' }}>
                        <img
                            src={imageUrl}
                            width="70"
                            alt={`product_${params.row.id}`}
                        />
                    </Box>
                );
            },
            sortable: false,
        },
        { field: 'name', headerName: 'Product Name', flex: 0.5 },
        { field: 'term', headerName: 'Contract Term', flex: 0.5, sortable: false },
        {
            field: 'connectivity',
            headerName: 'Connectivity',
            flex: 0.5,
            sortable: false,
        },
        {
            field: 'action',
            headerName: '',
            minWidth: 350,
            renderCell: () => {
                return (
                    <Box className="btn-medium-with-icon btn-dark">
                        <Box className="flex" sx={{ padding: '2px 4px', cursor: 'pointer' }}>
                            <AddIcon sx={{ marginRight: '3px' }} />
                            <Box>
                                <Box sx={{ marginTop: '1px', fontSize: '16px' }}>Add</Box>
                            </Box>
                        </Box>
                    </Box>
                );
            },
            sortable: false,
        },
    ];

    const {
        paginationModel,
        setPrimaryProduct,
        primaryProductList,
        selectedPrimaryProductIds,
        secondaryProductList,
        selectedSecondaryProductIds,
        selectedProducts,
        expanded,
        errorMessage,
        openSaveModal,
        showEditProduct,
        disabledOfferName,
        selectedPartners,
        setSelectedPartners,
        open,
        onSubmit,
        loadingUpdatePricingOffer,
        validationSchema,
        getTerminalPricing,
        handleSetOfferStatus,
        handleOpenSaveModal,
        handleSaveRecord,
        handleFindPrimaryProducts,
        handlePricingTypeOnChange,
        enableProductForPromo,
        handleSelectedPrimaryProductsIds,
        handleSelectedSecondaryProductsIds,
        handleAccordionChange,
        handleMultiplePricingOnChange,
        handleCloseModal,
        handleCloseSaveModal,
        handlePaginationModelChange,
        handlePricingLimitChange,
        rowCountState,
    } = usePricingsOffers(Number(id), PricingType.ONLINE, true);

    if (getTerminalPricing.loading) return <Loader />;

    const {
        data: { offer },
    } = getTerminalPricing;

    const initialValues = {
        id: offer?.id,
        name: offer?.name,
        term: offer?.term,
        partnerIds: offer?.partners.map(({ id }) => {
            return parseInt(id, 10);
        }),
        type: offer?.type,
        status: offer?.status,
        customTerm: parseInt(offer?.customTerm, 10),
        primaryProductIds: [],
        secondaryProductIds: [],
        salesConditions: offer?.salesConditions,
        mccConditions: offer?.mccConditions,
        gpvConditions: offer?.gpvConditions,
        allPartners: offer?.allPartners,
        gpvHighest: offer?.gpvHighest,
        gpvLowest: offer?.gpvLowest,
        mccs: offer?.mccs.map(({ id, code, name }) => {
            return {
                key: id,
                value: `${code} - ${name}`,
                label: `${code} - ${name}`,
            };
        }),
    };

    return (
        <PageContent heading="Edit Online Service Pricing offers">
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {({ values, setFieldValue }) => {
                    return (
                        <Form>
                            <Stack spacing={5}>
                                <MainCard>
                                    <h2 className="font-normal mb-0 mt-10">
                                        Pricing Offer Information
                                    </h2>
                                    <Box className="mt-20">
                                        <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <InputBaseField
                                                            label="Pricing Offer Name"
                                                            disabled={disabledOfferName}
                                                            placeholder="Enter Pricing offer name"
                                                            name="name"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <Box>
                                                            <Typography fontWeight={600}>
                                                                Contract Term
                                                            </Typography>
                                                            {offer?.term === PricingTerm.CUSTOM ? (
                                                                <Typography
                                                                    variant="h3"
                                                                    className="mt-10 font-normal black-200"
                                                                >
                                                                    {`${
                                                                        offer?.term.split('_')[0]
                                                                    }`.slice(0)}
                                                                    {` - ${offer.customTerm} Months`}
                                                                </Typography>
                                                            ) : (
                                                                <Typography
                                                                    variant="h3"
                                                                    className="mt-10 font-normal black-200"
                                                                >
                                                                    {`${
                                                                        offer?.term.split('_')[0]
                                                                    }`.slice(1)}{' '}
                                                                    Months
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <SelectBaseField
                                                            label="Offer Status"
                                                            name="status"
                                                            inputLabel={`${offer?.status}`}
                                                            initialValue={`${offer?.status}`}
                                                            setSelectedValue={handleSetOfferStatus}
                                                            menuItems={Object.values(
                                                                OfferStatus
                                                            ).map((value) => {
                                                                return {
                                                                    key: value.replace(/_/g, ' '),
                                                                    value,
                                                                };
                                                            })}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <Box>
                                                            <Typography fontWeight={600}>
                                                                Partners
                                                            </Typography>
                                                            <Typography
                                                                variant="h3"
                                                                className="mt-10 font-normal black-200"
                                                            >
                                                                {offer?.partners.map((partner) => {
                                                                    return (
                                                                        <Typography
                                                                            key={partner?.id}
                                                                            sx={{
                                                                                fontSize: '16px',
                                                                                marginBottom: '5px',
                                                                            }}
                                                                        >
                                                                            {
                                                                                partner?.business
                                                                                    ?.legalName
                                                                            }
                                                                        </Typography>
                                                                    );
                                                                })}
                                                                {!offer?.partners.length && (
                                                                    <span>N/A</span>
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            {showEditProduct && (
                                                <Box>
                                                    <Typography
                                                        variant="h3"
                                                        style={{ color: '#F18F34' }}
                                                    >
                                                        Primary Product
                                                    </Typography>
                                                    <Stack>
                                                        <Typography fontWeight={600} mt={2}>
                                                            Search Primary Product
                                                        </Typography>
                                                        <Box className="mt-10">
                                                            <Grid container spacing={3}>
                                                                <Grid item xs={12} md={6}>
                                                                    <Grid container>
                                                                        <Grid
                                                                            item
                                                                            xs={9}
                                                                            className="flex-col"
                                                                        >
                                                                            <InputBase
                                                                                name="primaryProduct"
                                                                                className={`form-input-group ${
                                                                                    errorMessage
                                                                                        ? 'input-error'
                                                                                        : ''
                                                                                }`}
                                                                                onChange={(
                                                                                    event
                                                                                ) => {
                                                                                    return setPrimaryProduct(
                                                                                        event.target
                                                                                            .value
                                                                                    );
                                                                                }}
                                                                            />
                                                                        </Grid>
                                                                        <Grid
                                                                            item
                                                                            xs={3}
                                                                            className="flex-col"
                                                                        >
                                                                            <Button
                                                                                variant="outlined"
                                                                                onClick={() => {
                                                                                    return handleFindPrimaryProducts(
                                                                                        {
                                                                                            page: paginationModel,
                                                                                        }
                                                                                    );
                                                                                }}
                                                                                className={`btn-input-group btn-primary ${
                                                                                    errorMessage
                                                                                        ? 'btn-error'
                                                                                        : ''
                                                                                }`}
                                                                            >
                                                                                Search
                                                                            </Button>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                        {errorMessage && (
                                                            <label className="error">
                                                                {errorMessage}
                                                            </label>
                                                        )}
                                                        {primaryProductList.length > 0 && (
                                                            <Box
                                                                sx={{
                                                                    marginTop: '20px',
                                                                    border: 'thin solid #F18F34',
                                                                    borderRadius: '10px',
                                                                }}
                                                            >
                                                                <DataGrid
                                                                    sx={{
                                                                        '.MuiDataGrid-columnHeaders':
                                                                            {
                                                                                backgroundColor:
                                                                                    '#F39E4E',
                                                                                color: '#ffffff',
                                                                                borderRadius:
                                                                                    '10px 10px 0 0',
                                                                            },
                                                                    }}
                                                                    autoHeight
                                                                    rows={primaryProductList}
                                                                    columns={columns}
                                                                    getRowHeight={() => {
                                                                        return 'auto';
                                                                    }}
                                                                    paginationModel={
                                                                        paginationModel
                                                                    }
                                                                    paginationMode="server"
                                                                    disableColumnMenu
                                                                    disableColumnFilter
                                                                    onRowSelectionModelChange={(
                                                                        ids
                                                                    ) => {
                                                                        return handleSelectedPrimaryProductsIds(
                                                                            ids
                                                                        );
                                                                    }}
                                                                    pageSizeOptions={[5, 10, 25]}
                                                                    rowCount={rowCountState}
                                                                    onPaginationModelChange={
                                                                        handlePaginationModelChange
                                                                    }
                                                                    checkboxSelection
                                                                    rowSelectionModel={
                                                                        selectedPrimaryProductIds
                                                                    }
                                                                />
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                    {secondaryProductList.length > 0 && (
                                                        <Stack>
                                                            <h3
                                                                className="font-bold mt-40 mb-10"
                                                                style={{ color: '#FDC800' }}
                                                            >
                                                                Secondary Product
                                                            </h3>
                                                            <Box
                                                                sx={{
                                                                    ...DEFAULT_BORDER,
                                                                }}
                                                            >
                                                                <DataGrid
                                                                    autoHeight
                                                                    rows={secondaryProductList}
                                                                    columns={columns}
                                                                    autoPageSize
                                                                    pageSizeOptions={[5]}
                                                                    getRowHeight={() => {
                                                                        return 'auto';
                                                                    }}
                                                                    paginationModel={
                                                                        paginationModel
                                                                    }
                                                                    disableColumnFilter
                                                                    onRowSelectionModelChange={(
                                                                        ids
                                                                    ) => {
                                                                        return handleSelectedSecondaryProductsIds(
                                                                            ids
                                                                        );
                                                                    }}
                                                                    checkboxSelection
                                                                    rowSelectionModel={
                                                                        selectedSecondaryProductIds
                                                                    }
                                                                />
                                                            </Box>
                                                        </Stack>
                                                    )}
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>
                                </MainCard>
                                {showEditProduct && selectedProducts.length > 0 && (
                                    <>
                                        <MainCard>
                                            <Box className="plain-bottom-border">
                                                <h2 className="font-normal mb-0 mt-10">Pricing</h2>
                                            </Box>
                                            {selectedPrimaryProductIds.length > 0 && (
                                                <Box sx={{ marginTop: '30px' }}>
                                                    <Typography
                                                        variant="h3"
                                                        style={{ color: '#F18F34' }}
                                                    >
                                                        Primary Product
                                                    </Typography>

                                                    <PricingAccordion
                                                        products={selectedProducts.filter(
                                                            (product) => {
                                                                return (
                                                                    product.catalogue ===
                                                                    ProductCatalogue.PRIMARY
                                                                );
                                                            }
                                                        )}
                                                        handleChange={handleAccordionChange}
                                                        expanded={expanded}
                                                        handlePricingTypeOnChange={
                                                            handlePricingTypeOnChange
                                                        }
                                                        handleMultiplePricingOnChange={
                                                            handleMultiplePricingOnChange
                                                        }
                                                        handlePricingLimitChange={
                                                            handlePricingLimitChange
                                                        }
                                                        handleApplyPromo={enableProductForPromo}
                                                        styling="primary"
                                                    />
                                                </Box>
                                            )}

                                            {selectedSecondaryProductIds.length > 0 && (
                                                <Box sx={{ marginTop: '30px' }}>
                                                    <Typography
                                                        variant="h3"
                                                        style={{ color: '#FDC800' }}
                                                    >
                                                        Secondary Product
                                                    </Typography>
                                                    <PricingAccordion
                                                        products={selectedProducts.filter(
                                                            (product) => {
                                                                return (
                                                                    product.catalogue ===
                                                                    'SECONDARY'
                                                                );
                                                            }
                                                        )}
                                                        handleChange={handleAccordionChange}
                                                        expanded={expanded}
                                                        handlePricingTypeOnChange={
                                                            handlePricingTypeOnChange
                                                        }
                                                        handleMultiplePricingOnChange={
                                                            handleMultiplePricingOnChange
                                                        }
                                                        handlePricingLimitChange={
                                                            handlePricingLimitChange
                                                        }
                                                        handleApplyPromo={enableProductForPromo}
                                                        styling="secondary"
                                                    />
                                                </Box>
                                            )}
                                        </MainCard>
                                        <PricingSalesConditions
                                            salesConditions={values.salesConditions}
                                            gpvConditions={values.gpvConditions}
                                            mccConditions={values.mccConditions}
                                            updatePricingConditions={(
                                                fieldName: string,
                                                fieldValue: boolean
                                            ) => {
                                                return setFieldValue(fieldName, fieldValue);
                                            }}
                                        />
                                    </>
                                )}

                                <TerminalPricingOfferPartnersModal
                                    open={open}
                                    handleCloseModal={handleCloseModal}
                                    selectedPartners={selectedPartners}
                                    setSelectedPartners={setSelectedPartners}
                                />

                                <Box>
                                    <div className="flex space-end">
                                        <Button
                                            onClick={() => {
                                                return navigate('/online-pricing');
                                            }}
                                            className="reset-btn btn-medium mr-20"
                                            variant="contained"
                                            size="medium"
                                            disableElevation
                                        >
                                            Cancel
                                        </Button>
                                        {loadingUpdatePricingOffer ? (
                                            <LoadingButton
                                                className="btn-medium"
                                                style={{
                                                    color: '#fff',
                                                    padding: '5px 30px 5px 10px',
                                                }}
                                                loading
                                                loadingPosition="start"
                                                startIcon={<SaveIcon />}
                                                variant="contained"
                                            >
                                                Save
                                            </LoadingButton>
                                        ) : (
                                            <Box className="btn-wrapper">
                                                <Button
                                                    onClick={(event) => {
                                                        return handleOpenSaveModal(event, values);
                                                    }}
                                                    className={`btn-primary btn-medium`}
                                                    disabled={loadingUpdatePricingOffer}
                                                    type="button"
                                                    variant="contained"
                                                    size="medium"
                                                    disableElevation
                                                >
                                                    Save
                                                </Button>
                                            </Box>
                                        )}
                                    </div>
                                </Box>
                                <Box>
                                    <Dialog
                                        open={openSaveModal}
                                        onClose={handleCloseSaveModal}
                                        aria-labelledby="delete-user-dialog-title"
                                        sx={{
                                            '& .MuiDialog-paper': {
                                                width: '400px',
                                            },
                                        }}
                                    >
                                        <DialogContent>
                                            <ModalContent
                                                handleClose={handleCloseSaveModal}
                                                handleAction={handleSaveRecord}
                                                header="Save Terminal Pricing?"
                                                isEdit
                                                message={
                                                    values.status === OfferStatus.ACTIVE
                                                        ? updatePricingAlertMessage
                                                        : deactivatePricingAlertMessage
                                                }
                                                buttonText="Save"
                                            />
                                        </DialogContent>
                                    </Dialog>
                                </Box>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default EditOnlinePricingPage;
